import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Camera, Upload, RefreshCw, Sparkles, ShieldAlert, Mail, Sun, X, Check, AlertTriangle,
} from "lucide-react";
import { SectionLabel } from "@/components/Reveal";
import DemographicsFields from "@/components/DemographicsFields";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LANGUAGES = [
  "English","Hindi","Bengali","Marathi","Telugu","Tamil","Gujarati",
  "Kannada","Malayalam","Punjabi","Odia","Assamese","Urdu",
];

const SUNSCREEN_TYPES = [
  { value: "chemical", label: "Chemical (translucent)", hint: "Absorbs UV — usually invisible on skin" },
  { value: "mineral", label: "Mineral (zinc/titanium)", hint: "Whitecast possible — coverage read disabled" },
  { value: "tinted", label: "Tinted / BB / mineral tint", hint: "Coverage read disabled — safety first" },
  { value: "unknown", label: "I'm not sure", hint: "We'll adapt the read accordingly" },
  { value: "none", label: "None yet — before application", hint: "We'll show you the zones to cover" },
];

// ---- shared image helpers (subset of SkinAnalysis) --------------------------
function loadImage(src) {
  return new Promise((res, rej) => { const i = new Image(); i.onload = () => res(i); i.onerror = rej; i.src = src; });
}
async function processDataUrl(dataUrl) {
  const img = await loadImage(dataUrl);
  const maxDim = 720;
  let { width, height } = img;
  const scale = Math.min(1, maxDim / Math.max(width, height));
  width = Math.round(width * scale); height = Math.round(height * scale);
  const canvas = document.createElement("canvas");
  canvas.width = width; canvas.height = height;
  canvas.getContext("2d").drawImage(img, 0, 0, width, height);
  const s = 180;
  const qc = document.createElement("canvas");
  qc.width = s; qc.height = s;
  const qctx = qc.getContext("2d");
  qctx.drawImage(img, 0, 0, s, s);
  const { data } = qctx.getImageData(0, 0, s, s);
  const gray = new Float32Array(s * s);
  let sum = 0;
  for (let i = 0; i < s * s; i++) {
    const r = data[i * 4], g = data[i * 4 + 1], b = data[i * 4 + 2];
    const v = 0.299 * r + 0.587 * g + 0.114 * b;
    gray[i] = v; sum += v;
  }
  const brightness = sum / (s * s);
  let lapSum = 0, lapSq = 0, n = 0;
  for (let y = 1; y < s - 1; y++)
    for (let x = 1; x < s - 1; x++) {
      const idx = y * s + x;
      const lap = 4 * gray[idx] - gray[idx - 1] - gray[idx + 1] - gray[idx - s] - gray[idx + s];
      lapSum += lap; lapSq += lap * lap; n++;
    }
  const mean = lapSum / n;
  const blurVar = lapSq / n - mean * mean;
  return { dataUrl: canvas.toDataURL("image/jpeg", 0.82), brightness, blurVar };
}
function qualityIssue({ brightness, blurVar }) {
  if (brightness < 42) return "Photo looks too dark — try again in brighter, even light.";
  if (brightness > 245) return "Photo looks overexposed — reduce direct light and retake.";
  if (blurVar < 45) return "Photo looks blurry — hold steady and retake.";
  return null;
}

function ScoreRing({ label, value, disabled }) {
  const R = 70, C = 2 * Math.PI * R;
  const [v, setV] = useState(0);
  useEffect(() => { const t = setTimeout(() => setV(disabled ? 0 : value), 200); return () => clearTimeout(t); }, [value, disabled]);
  return (
    <div className="relative w-[160px] h-[160px]" data-testid={`sun-ring-${label.toLowerCase().replace(/\s/g,'-')}`}>
      <svg viewBox="0 0 160 160" className="w-full h-full -rotate-90">
        <circle cx="80" cy="80" r={R} fill="none" stroke="#e5d9c5" strokeWidth="10" />
        {!disabled && (
          <circle cx="80" cy="80" r={R} fill="none" stroke="url(#sg)" strokeWidth="10" strokeLinecap="round"
            strokeDasharray={C} strokeDashoffset={C - (v / 100) * C}
            style={{ transition: "stroke-dashoffset 1.4s cubic-bezier(0.16,1,0.3,1)" }} />
        )}
        <defs>
          <linearGradient id="sg" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor="#c5a059" />
            <stop offset="100%" stopColor="#a9863f" />
          </linearGradient>
        </defs>
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <div className="font-serif text-4xl text-[color:var(--relumia-ink)]">{disabled ? "—" : value}</div>
        <div className="overline mt-1 text-center px-2">{label}</div>
      </div>
    </div>
  );
}

const STATUS_STYLE = {
  covered:  { bg: "bg-emerald-50", ring: "border-emerald-300", text: "text-emerald-700", icon: Check, label: "Covered" },
  missed:   { bg: "bg-amber-50",   ring: "border-amber-300",   text: "text-amber-800",   icon: AlertTriangle, label: "Missed" },
  uncertain:{ bg: "bg-surface",    ring: "border-champagne",   text: "text-[color:var(--relumia-muted)]", icon: ShieldAlert, label: "Check" },
};

export default function SunscreenCoach() {
  const [stage, setStage] = useState("intro"); // intro | camera | preview | loading | result
  const [preview, setPreview] = useState(null);
  const [result, setResult] = useState(null);
  const [sType, setSType] = useState("chemical");
  const [language, setLanguage] = useState("English");
  const [view, setView] = useState("en"); // en | loc
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [demo, setDemo] = useState({ gender: "", age: "", city: "", location: "" });
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const fileRef = useRef(null);

  const stopCamera = () => {
    if (streamRef.current) { streamRef.current.getTracks().forEach(t => t.stop()); streamRef.current = null; }
  };
  useEffect(() => () => stopCamera(), []);

  const startCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: "user", width: { ideal: 1280 }, height: { ideal: 1280 } }, audio: false });
      streamRef.current = stream;
      setStage("camera");
      setTimeout(() => { if (videoRef.current) { videoRef.current.srcObject = stream; videoRef.current.play().catch(() => {}); } }, 60);
    } catch { toast.error("Couldn't access the camera. You can upload a photo instead."); }
  };

  const ingest = async (dataUrl) => {
    try {
      const q = await processDataUrl(dataUrl);
      const issue = qualityIssue(q);
      if (issue) { toast.error(issue); return; }
      setPreview(q.dataUrl); setStage("preview");
    } catch { toast.error("Couldn't read that image. Please try another."); }
  };

  const capture = async () => {
    const v = videoRef.current; if (!v) return;
    const canvas = document.createElement("canvas");
    canvas.width = v.videoWidth; canvas.height = v.videoHeight;
    canvas.getContext("2d").drawImage(v, 0, 0);
    const dataUrl = canvas.toDataURL("image/jpeg", 0.9);
    stopCamera();
    await ingest(dataUrl);
  };

  const onUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!/image\/(jpeg|jpg|png|webp)/.test(file.type)) { toast.error("Please upload a JPG, PNG or WEBP image."); return; }
    const reader = new FileReader();
    reader.onload = () => ingest(reader.result);
    reader.readAsDataURL(file);
    e.target.value = "";
  };

  const analyze = async () => {
    if (!preview) return;
    if (name.trim().length < 2) return toast.error("Please enter your name.");
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) return toast.error("Please enter a valid email address.");
    if (phone.replace(/\D/g, "").length < 7) return toast.error("Please enter a valid mobile number.");
    setStage("loading");
    try {
      const { data } = await axios.post(`${API}/sunscreen-analysis`, {
        image: preview,
        name: name.trim(), email: email.trim(), phone: phone.trim(),
        sunscreen_type: sType,
        language,
        gender: demo.gender || null,
        age: demo.age ? Number(demo.age) : null,
        city: demo.city || null,
        location: demo.location || null,
      });
      if (!data.valid) { toast.error(data.issue || "We couldn't read your photo clearly. Please retake."); setStage("preview"); return; }
      setResult(data); setView("en"); setStage("result");
    } catch (err) {
      const status = err?.response?.status;
      const msg = err?.response?.data?.detail || (status === 429 ? "Analysis limit reached. Please try again later." : "Analysis failed. Please try a clearer, well-lit selfie.");
      toast.error(msg); setStage("preview");
    }
  };

  const reset = () => {
    stopCamera(); setPreview(null); setResult(null); setName(""); setEmail(""); setPhone(""); setSType("chemical"); setLanguage("English"); setView("en"); setDemo({ gender: "", age: "", city: "", location: "" }); setStage("intro");
  };

  // localized-view helpers
  const hasLoc = Boolean(result?.translation && result.translation.summary);
  const loc = view === "loc" && hasLoc ? result.translation : null;
  const D = loc
    ? {
        summary: loc.summary || result.summary,
        high_risk_zones: loc.zones?.length ? loc.zones : result.high_risk_zones,
        tips: loc.tips?.length ? loc.tips : result.tips,
        reapplication_advice: loc.reapplication_advice || result.reapplication_advice,
        disclosure: loc.disclosure || result.disclosure,
        product_advice: loc.product_advice?.length ? loc.product_advice : result.product_advice,
      }
    : result;

  return (
    <section id="sunscreen-coach" data-testid="sunscreen-section" className="relative z-10 py-24 md:py-32 bg-[color:var(--relumia-ink)] text-[color:var(--relumia-base)] overflow-hidden">
      <div className="pointer-events-none absolute inset-0">
        <div className="absolute -left-32 top-16 h-[420px] w-[420px] rounded-full bg-[color:var(--relumia-gold)]/10 blur-[120px]" />
        <div className="absolute -right-32 bottom-16 h-[520px] w-[520px] rounded-full bg-[color:var(--relumia-gold)]/8 blur-[140px]" />
      </div>

      <div className="relative mx-auto max-w-[1400px] px-5 md:px-10">
        <div className="grid lg:grid-cols-[0.42fr_0.58fr] gap-10 lg:gap-16 items-start">
          {/* Intro */}
          <div className="lg:sticky lg:top-24">
            <div className="flex items-center gap-3 mb-4">
              <Sun size={18} className="text-gold" strokeWidth={1.6} />
              <span className="overline !text-gold">Relumia SunGuard · Beta</span>
            </div>
            <h2 className="font-serif font-light tracking-tight leading-[1.02] text-4xl md:text-5xl lg:text-[3.4rem]">
              Are you missing <span className="italic text-gold">sunscreen spots?</span>
            </h2>
            <p className="mt-6 text-[15px] leading-relaxed text-white/70 max-w-md">
              A quick visible-light coaching check. Take a selfie, tell us what you applied,
              and we'll flag high-risk zones people typically miss — eyelids, medial canthus,
              nose bridge, ears and hairline.
            </p>
            <div className="mt-6 rounded-sm bg-white/[0.04] border border-white/10 p-4 max-w-md">
              <div className="flex items-start gap-3">
                <ShieldAlert size={18} className="text-gold shrink-0 mt-0.5" strokeWidth={1.6} />
                <p className="text-[12px] leading-relaxed text-white/70">
                  <b className="text-white">Important:</b> This is not a UV camera. Chemical
                  sunscreens leave a faint sheen we can coach on; mineral & tinted formulas
                  don't show up in visible light — for those we'll skip the coverage verdict
                  and give you a check-yourself guide instead. Wellness use only; not a
                  medical diagnosis.
                </p>
              </div>
            </div>
          </div>

          {/* Panel */}
          <div className="bg-white/[0.03] border border-white/10 backdrop-blur-sm rounded-sm p-6 md:p-9 min-h-[420px]">
            {stage === "intro" && (
              <div className="flex flex-col" data-testid="sun-intro">
                <div className="text-center py-4">
                  <div className="mx-auto flex h-20 w-20 items-center justify-center rounded-full bg-white/5 border border-white/10 mb-6">
                    <Sun size={34} className="text-gold" strokeWidth={1.3} />
                  </div>
                  <h3 className="font-serif text-2xl text-white">Start your coverage check</h3>
                  <p className="text-[13.5px] text-white/60 mt-2 max-w-sm mx-auto">Even, natural light works best. Look straight ahead so all zones are visible.</p>
                </div>

                <div className="mt-4">
                  <label className="overline !text-white/60 mb-3 block">What did you apply?</label>
                  <div className="grid sm:grid-cols-2 gap-2.5">
                    {SUNSCREEN_TYPES.map((t) => {
                      const active = sType === t.value;
                      return (
                        <button
                          key={t.value}
                          onClick={() => setSType(t.value)}
                          data-testid={`sun-type-${t.value}`}
                          className={`text-left rounded-sm border px-4 py-3 transition-all ${active ? "border-[color:var(--relumia-gold)] bg-[color:var(--relumia-gold)]/8" : "border-white/10 bg-white/[0.02] hover:border-[color:var(--relumia-gold)]/40"}`}
                        >
                          <div className="text-[13px] text-white flex items-center gap-2">
                            <span className={`h-2 w-2 rounded-full ${active ? "bg-[color:var(--relumia-gold)]" : "bg-white/25"}`} />
                            {t.label}
                          </div>
                          <div className="text-[11.5px] text-white/50 mt-1">{t.hint}</div>
                        </button>
                      );
                    })}
                  </div>
                </div>

                <div className="mt-8 flex flex-col sm:flex-row gap-3">
                  <button onClick={startCamera} data-testid="sun-start-camera" className="flex-1 inline-flex items-center justify-center gap-2.5 rounded-full bg-[color:var(--relumia-gold)] px-6 py-3.5 text-[13px] tracking-wide text-[color:var(--relumia-ink)] hover:bg-[color:var(--relumia-gold-deep)] hover:text-white transition-colors">
                    <Camera size={17} strokeWidth={1.8} /> Take a selfie
                  </button>
                  <button onClick={() => fileRef.current?.click()} data-testid="sun-upload-btn" className="flex-1 inline-flex items-center justify-center gap-2.5 rounded-full border border-white/25 px-6 py-3.5 text-[13px] tracking-wide text-white hover:border-[color:var(--relumia-gold)] transition-colors">
                    <Upload size={17} strokeWidth={1.8} /> Upload photo
                  </button>
                </div>
                <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp" onChange={onUpload} className="hidden" data-testid="sun-upload-input" />
              </div>
            )}

            {stage === "camera" && (
              <div className="flex flex-col items-center" data-testid="sun-camera">
                <div className="relative w-full max-w-[420px] aspect-square rounded-sm overflow-hidden bg-black">
                  <video ref={videoRef} playsInline muted className="w-full h-full object-cover -scale-x-100" />
                  <div className="absolute inset-0 pointer-events-none flex items-center justify-center">
                    <div className="w-[62%] h-[80%] rounded-[50%] border-2 border-white/70 border-dashed" />
                  </div>
                </div>
                <div className="mt-6 flex gap-3">
                  <button onClick={capture} data-testid="sun-capture-btn" className="inline-flex items-center gap-2.5 rounded-full bg-[color:var(--relumia-gold)] px-7 py-3.5 text-[13px] tracking-wide text-[color:var(--relumia-ink)] hover:bg-[color:var(--relumia-gold-deep)] hover:text-white transition-colors">
                    <Camera size={17} strokeWidth={1.8} /> Capture
                  </button>
                  <button onClick={reset} className="inline-flex items-center gap-2 rounded-full border border-white/25 px-6 py-3.5 text-[13px] text-white hover:border-[color:var(--relumia-gold)] transition-colors">
                    <X size={16} /> Cancel
                  </button>
                </div>
              </div>
            )}

            {stage === "preview" && preview && (
              <div className="flex flex-col items-center" data-testid="sun-preview">
                <div className="w-full max-w-[360px] aspect-square rounded-sm overflow-hidden border border-white/15">
                  <img src={preview} alt="Selfie preview" className="w-full h-full object-cover" />
                </div>
                <p className="mt-6 text-[12.5px] text-white/60 text-center max-w-[380px]">
                  Add your details — we'll email you the full coverage report and application tips.
                </p>
                {[
                  ["text", name, setName, "Your name*", "sun-name-input"],
                  ["email", email, setEmail, "Your email*", "sun-email-input"],
                  ["tel", phone, setPhone, "Your mobile number*", "sun-phone-input"],
                ].map(([type, val, set, ph, tid], i) => (
                  <input key={tid} type={type} value={val} onChange={(e) => set(e.target.value)} placeholder={ph} data-testid={tid}
                    className={`${i === 0 ? "mt-4" : "mt-3"} w-full max-w-[380px] bg-transparent border-b border-white/25 py-2.5 text-[14px] text-white placeholder:text-white/40 focus:outline-none focus:border-[color:var(--relumia-gold)]`} />
                ))}
                <div className="mt-4 w-full max-w-[380px]">
                  <label className="overline !text-white/60 block mb-2">Report language (English + selected)</label>
                  <select
                    value={language}
                    onChange={(e) => setLanguage(e.target.value)}
                    data-testid="sun-language-select"
                    className="w-full bg-white/[0.04] border border-white/15 rounded-sm py-2.5 px-3 text-[14px] text-white focus:outline-none focus:border-[color:var(--relumia-gold)]"
                  >
                    {LANGUAGES.map((l) => (
                      <option key={l} value={l} className="text-[color:var(--relumia-ink)]">
                        {l === "English" ? "English only" : `English + ${l}`}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="w-full max-w-[380px]">
                  <DemographicsFields values={demo} onChange={setDemo} dark tidPrefix="sun-demo" />
                </div>
                <div className="mt-6 flex gap-3">
                  <button onClick={analyze} data-testid="sun-analyze-btn" className="inline-flex items-center gap-2.5 rounded-full bg-[color:var(--relumia-gold)] px-8 py-3.5 text-[13px] tracking-wide text-[color:var(--relumia-ink)] hover:bg-[color:var(--relumia-gold-deep)] hover:text-white transition-colors">
                    <Sparkles size={16} strokeWidth={1.8} /> Check my coverage
                  </button>
                  <button onClick={reset} data-testid="sun-retake-btn" className="inline-flex items-center gap-2 rounded-full border border-white/25 px-6 py-3.5 text-[13px] text-white hover:border-[color:var(--relumia-gold)] transition-colors">
                    <RefreshCw size={15} /> Retake
                  </button>
                </div>
              </div>
            )}

            {stage === "loading" && (
              <div className="flex flex-col items-center justify-center py-24 text-center" data-testid="sun-loading">
                <div className="relative h-16 w-16">
                  <div className="absolute inset-0 rounded-full border-2 border-white/15" />
                  <div className="absolute inset-0 rounded-full border-2 border-transparent border-t-[color:var(--relumia-gold)] animate-spin" />
                </div>
                <p className="font-serif text-2xl text-white mt-6">Reading your coverage…</p>
                <p className="text-[13px] text-white/60 mt-1">Mapping high-risk zones</p>
              </div>
            )}

            {stage === "result" && result && (
              <div data-testid="sun-result">
                {hasLoc && (
                  <div className="flex justify-center sm:justify-end mb-5" data-testid="sun-lang-toggle">
                    <div className="inline-flex rounded-full border border-white/15 bg-white/[0.04] p-1">
                      <button onClick={() => setView("en")} data-testid="sun-lang-en"
                        className={`px-4 py-1.5 rounded-full text-[12px] tracking-wide transition-colors ${view === "en" ? "bg-[color:var(--relumia-gold)] text-[color:var(--relumia-ink)]" : "text-white/70"}`}>
                        English
                      </button>
                      <button onClick={() => setView("loc")} data-testid="sun-lang-loc"
                        className={`px-4 py-1.5 rounded-full text-[12px] tracking-wide transition-colors ${view === "loc" ? "bg-[color:var(--relumia-gold)] text-[color:var(--relumia-ink)]" : "text-white/70"}`}>
                        {result.language}
                      </button>
                    </div>
                  </div>
                )}
                {/* Rings */}
                <div className="flex flex-col sm:flex-row items-center gap-8 pb-8 border-b border-white/10">
                  <div className="flex gap-6">
                    <ScoreRing label="Coverage" value={result.coverage_score} disabled={!result.coverage_available} />
                    <ScoreRing label="Uniformity" value={result.uniformity_score} disabled={!result.coverage_available} />
                  </div>
                  <div className="flex-1 text-center sm:text-left">
                    <div className="overline !text-gold mb-2">You applied · {result.sunscreen_type}</div>
                    <p className="text-[14px] leading-relaxed text-white/85">{D.summary}</p>
                  </div>
                </div>

                {/* Verdict-unavailable banner */}
                {!result.coverage_available && (
                  <div className="mt-6 flex items-start gap-3 rounded-sm bg-amber-950/30 border border-amber-500/30 p-5" data-testid="sun-verdict-off">
                    <AlertTriangle size={18} className="text-amber-400 shrink-0 mt-0.5" strokeWidth={1.6} />
                    <p className="text-[13px] leading-relaxed text-amber-100/90">
                      <b>Coverage verdict disabled.</b> {D.disclosure}
                    </p>
                  </div>
                )}

                {/* Zones */}
                {D.high_risk_zones?.length > 0 && (
                  <div className="mt-8">
                    <div className="overline !text-white/60 mb-4">High-risk zones</div>
                    <div className="grid sm:grid-cols-2 gap-3">
                      {D.high_risk_zones.map((z, i) => {
                        const style = STATUS_STYLE[z.status] || STATUS_STYLE.uncertain;
                        const Icon = style.icon;
                        return (
                          <div key={i} data-testid={`sun-zone-${i}`} className={`rounded-sm border ${style.ring} bg-white/[0.02] p-4`}>
                            <div className="flex items-start gap-3">
                              <div className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full border ${style.ring} ${style.bg}`}>
                                <Icon size={14} className={style.text} strokeWidth={2} />
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="flex items-baseline justify-between gap-2">
                                  <div className="text-[13.5px] text-white truncate">{z.zone}</div>
                                  <span className={`text-[10px] tracking-widest uppercase ${style.text}`}>{style.label}</span>
                                </div>
                                {z.tip && <p className="text-[11.5px] text-white/60 mt-1 leading-snug">{z.tip}</p>}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Tips */}
                {D.tips?.length > 0 && (
                  <div className="mt-8">
                    <div className="overline !text-white/60 mb-3">Application tips</div>
                    <ul className="space-y-2">
                      {D.tips.map((t, i) => (
                        <li key={i} className="text-[13px] leading-relaxed text-white/80 flex gap-2"><span className="text-gold">✦</span>{t}</li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Reapply */}
                {D.reapplication_advice && (
                  <div className="mt-8 rounded-sm bg-[color:var(--relumia-gold)]/10 border border-[color:var(--relumia-gold)]/30 p-5" data-testid="sun-reapply">
                    <div className="overline !text-gold mb-2">Reapplication</div>
                    <p className="text-[13.5px] text-white leading-relaxed">{D.reapplication_advice}</p>
                  </div>
                )}

                {/* Product advice — Faciox UV + Relumia */}
                {D.product_advice?.length > 0 && (
                  <div className="mt-8" data-testid="sun-product-advice">
                    <div className="overline !text-gold mb-3">Recommended by JAKS Pharma</div>
                    <div className="grid sm:grid-cols-2 gap-4">
                      {D.product_advice.map((p, i) => (
                        <div key={i} data-testid={`sun-product-${i}`}
                          className="rounded-sm border border-[color:var(--relumia-gold)]/30 bg-gradient-to-br from-[color:var(--relumia-gold)]/10 to-transparent p-5">
                          <div className="overline !text-gold mb-1.5">{p.role}</div>
                          <div className="font-serif text-2xl text-white mb-2">{p.name}</div>
                          <p className="text-[12.5px] leading-relaxed text-white/75">{p.pitch}</p>
                        </div>
                      ))}
                    </div>
                    <p className="mt-3 text-[11px] text-white/40 leading-relaxed">
                      Faciox UV Sunscreen offers broad-spectrum topical protection. Relumia offers antioxidant support from within. Best combined with a professional dermatologist's guidance for your skin.
                    </p>
                  </div>
                )}

                {/* Footer disclaimer + retry */}
                <div className="mt-8 flex flex-col sm:flex-row items-center gap-3 justify-between">
                  <span className="text-[12px] text-white/50 inline-flex items-center gap-2" data-testid="sun-email-confirm">
                    <Mail size={14} className="text-gold" /> Full report{result.language !== "English" ? ` (English + ${result.language})` : ""} emailed{email ? ` to ${email}` : ""}.
                  </span>
                  <button onClick={reset} data-testid="sun-again-btn" className="inline-flex items-center gap-2 text-[12.5px] text-gold hover:text-white transition-colors">
                    <RefreshCw size={14} /> Check again
                  </button>
                </div>
                <p className="mt-6 text-[11px] text-white/40 leading-relaxed">
                  Wellness &amp; educational use only. This is a visible-light technique check, not a UV coverage measurement or a medical diagnosis. For any skin concerns, consult a dermatologist.
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
